import { useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Card from "../components/ui/Card";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const faqSections: FAQSection[] = [
  {
    title: "About the products",
    items: [
      {
        question: "What is the Buyer's Report?",
        answer:
          "A pre-offer due-diligence report on a UK property. It pulls together public records into eight sections: Energy Performance Certificate (EPC), flood risk, heritage designations, alteration and extension constraints (Green Belt, Areas of Outstanding Natural Beauty, Tree Preservation Orders, Article 4 directions), planning application history (last 10 years), and tax/regulatory rules (Stamp Duty Land Tax, council tax band, leasehold reform, Section 24). It costs £29 as a one-off payment. Title summary is not yet included — we will add it once HM Land Registry integration is live.",
      },
      {
        question: "What is the Seller's Pack?",
        answer:
          "A solicitor-ready PDF of the official conveyancing forms, prepared from a guided disclosure questionnaire and any documents you upload. Freehold properties get the TA6 Property Information Form. Leasehold properties also get the TA7 Leasehold Information Form and the LPE1 Leasehold Property Enquiries. Costs £79 as a one-off payment. Your solicitor reviews and submits the final version.",
      },
      {
        question: "Do I need an account to buy a Buyer's Report?",
        answer:
          "No. You can pay and download a Buyer's Report without creating an account. We offer to save it to an account after payment so you can come back to it later, but that step is optional.",
      },
      {
        question: "Is this legal advice?",
        answer:
          "No. UKPropertyChecks is an information and document preparation service. The Buyer's Report summarises public records; the Seller's Pack prepares draft answers for your solicitor to review. Neither is legal advice. Consult a qualified solicitor for legal matters relating to your property transaction.",
      },
      {
        question: "What is a TA6 form?",
        answer:
          "The TA6 is the Property Information Form used in property sales in England and Wales. It covers boundaries, disputes, planning permissions, building works, utilities, and environmental matters. Sellers complete it as part of the conveyancing process. We help you prepare draft answers based on documents you upload; your solicitor reviews the final version.",
      },
      {
        question: "What is a TA7 form?",
        answer:
          "The TA7 is the Leasehold Information Form. It applies only to leasehold properties and covers the lease, service charges, ground rent, management company, and planned works. Freehold properties do not need a TA7.",
      },
      {
        question: "What is an LPE1 form?",
        answer:
          "The LPE1 (Leasehold Property Enquiries) is normally completed by your managing agent or freeholder. It covers service charges, insurance, management company details, and lease variations. Our questionnaire lets you pre-fill what you know to speed up the process.",
      },
    ],
  },
  {
    title: "Process and timing",
    items: [
      {
        question: "How long does a Buyer's Report take?",
        answer:
          "Typically a few minutes after payment. We pull data from public sources and assemble the eight-section PDF. You can preview the property file (a free summary of public records) before paying.",
      },
      {
        question: "How long does a Seller's Pack take?",
        answer:
          "The disclosure questionnaire takes 15–30 minutes depending on how much you know about the property. Document uploads are optional but help us pre-fill more fields. After payment the PDF pack is generated within a few minutes.",
      },
      {
        question: "Do I have to answer every question on the questionnaire?",
        answer:
          "No. Answer what you can. Unanswered questions appear blank on the generated forms, and your solicitor can help fill in the gaps. The preview shows exactly which questions are unanswered before you pay.",
      },
      {
        question: "Can I update my Seller's Pack after generating it?",
        answer:
          "Yes. You can upload additional documents and regenerate your pack at any time. Each regeneration uses the latest answers and documents you have provided.",
      },
    ],
  },
  {
    title: "Payment and refunds",
    items: [
      {
        question: "How much do the products cost?",
        answer:
          "Buyer's Report: £29. Seller's Pack: £79. Both are one-off payments — no subscription. All prices are in GBP and include VAT where applicable.",
      },
      {
        question: "Can I get a refund?",
        answer:
          "Reports and packs are digital goods delivered immediately after payment, so refunds are generally not available after delivery. We may offer a refund at our discretion for a technical error preventing report generation, a duplicate purchase, or a service failure. Email support@ukpropertychecks.co.uk within 14 days to dispute a charge.",
      },
      {
        question: "What payment methods do you accept?",
        answer:
          "Major credit and debit cards through Stripe. We do not store your card details — payment processing is handled entirely by Stripe.",
      },
    ],
  },
  {
    title: "Data and privacy",
    items: [
      {
        question: "How is my data stored and protected?",
        answer:
          "All data is stored in AWS data centres in the UK (London region). Documents are encrypted at rest using AES-256 encryption and in transit using TLS 1.2. We do not sell or share your data with third parties for marketing purposes. See our Privacy Policy for full details.",
      },
      {
        question: "How long do you keep my documents?",
        answer:
          "Uploaded source documents are automatically deleted after 90 days. Generated Buyer's Reports and Seller's Packs remain accessible in your account for 12 months. You can request earlier deletion of your data at any time by emailing privacy@ukpropertychecks.co.uk.",
      },
    ],
  },
];

function AccordionItem({ item, isOpen, onToggle, id }: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  id: string;
}) {
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        id={`${id}-trigger`}
        aria-expanded={isOpen}
        aria-controls={`${id}-panel`}
        onClick={onToggle}
        className="flex w-full items-center justify-between py-4 text-left text-base font-semibold text-slate-950 hover:text-emerald-700 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#ffdd00] focus-visible:outline-offset-2 min-h-[44px]"
      >
        <span className="pr-4">{item.question}</span>
        <span aria-hidden="true" className="shrink-0 text-slate-400">
          {isOpen ? "−" : "+"}
        </span>
      </button>
      <div
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-trigger`}
        hidden={!isOpen}
      >
        <p className="pb-4 text-base leading-7 text-slate-800">
          {item.answer}
        </p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = useCallback((id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>FAQ | UKPropertyChecks</title>
        <meta
          name="description"
          content="Frequently asked questions about the UKPropertyChecks Buyer's Report (£29) and Seller's Pack (£79)."
        />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/faq" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqSections.flatMap((section) =>
              section.items.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.answer,
                },
              }))
            ),
          })}
        </script>
      </Helmet>

      <main className="bg-white">
        <div className="container-main section-spacing">
          <h1>Frequently Asked Questions</h1>
          <p className="mt-3 text-base leading-7 text-slate-800">
            Common questions about the Buyer's Report and Seller's Pack.
          </p>

          <div className="mt-8 space-y-6">
            {faqSections.map((section, sIdx) => (
              <Card key={sIdx}>
                <h2 className="text-lg font-semibold text-slate-950 mb-2">
                  {section.title}
                </h2>
                {section.items.map((item, iIdx) => {
                  const id = `faq-${sIdx}-${iIdx}`;
                  return (
                    <AccordionItem
                      key={id}
                      id={id}
                      item={item}
                      isOpen={openItems.has(id)}
                      onToggle={() => toggleItem(id)}
                    />
                  );
                })}
              </Card>
            ))}
          </div>

          <div className="mt-8">
            <Card variant="subtle">
              <p className="text-sm text-slate-600">
                See{" "}
                <Link
                  to="/pricing"
                  className="text-blue-700 underline hover:text-blue-800"
                >
                  pricing
                </Link>{" "}
                or{" "}
                <Link
                  to="/samples"
                  className="text-blue-700 underline hover:text-blue-800"
                >
                  sample reports
                </Link>
                . Still have questions?{" "}
                <Link
                  to="/contact"
                  className="text-blue-700 underline hover:text-blue-800"
                >
                  Contact our support team
                </Link>
                . We aim to respond within 2 working days.
              </p>
            </Card>
          </div>
        </div>
      </main>
    </>
  );
}
